/**
 * FeedbackButtons – Thumbs up / down + comment icons shown on bot messages.
 * Mirrors the rate-loader widget-UI feedback pattern.
 */
import React, { memo } from 'react';
import { ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';

interface FeedbackButtonsProps {
  messageId: string;
  reaction?: 'like' | 'dislike' | '';
  hasComment?: boolean;
  onLike: (messageId: string) => void;
  onDislike: (messageId: string) => void;
  onComment: (messageId: string) => void;
  isDark?: boolean;
}

const btnBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  borderRadius: '6px',
  padding: '4px',
  cursor: 'pointer',
  transition: 'background 0.15s, color 0.15s',
};

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  messageId,
  reaction,
  hasComment,
  onLike,
  onDislike,
  onComment,
  isDark,
}) => {
  const activeColor = '#196ECF';
  const inactiveColor = isDark ? '#9ca3af' : '#6b7280';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        marginTop: '8px',
      }}
    >
      {/* Thumbs Up */}
      <button
        type="button"
        title="Helpful"
        aria-label="Mark as helpful"
        style={{
          ...btnBase,
          color: reaction === 'like' ? activeColor : inactiveColor,
        }}
        onClick={() => onLike(messageId)}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = isDark
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.05)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'none';
        }}
      >
        <ThumbsUp
          size={16}
          strokeWidth={reaction === 'like' ? 2.5 : 1.8}
          fill={reaction === 'like' ? activeColor : 'none'}
        />
      </button>

      {/* Thumbs Down */}
      <button
        type="button"
        title="Not helpful"
        aria-label="Mark as not helpful"
        style={{
          ...btnBase,
          color: reaction === 'dislike' ? activeColor : inactiveColor,
        }}
        onClick={() => onDislike(messageId)}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = isDark
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.05)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'none';
        }}
      >
        <ThumbsDown
          size={16}
          strokeWidth={reaction === 'dislike' ? 2.5 : 1.8}
          fill={reaction === 'dislike' ? activeColor : 'none'}
        />
      </button>

      {/* Comment */}
      <button
        type="button"
        title="Add comment"
        aria-label="Add comment"
        style={{
          ...btnBase,
          color: hasComment ? activeColor : inactiveColor,
        }}
        onClick={() => onComment(messageId)}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = isDark
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.05)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'none';
        }}
      >
        <MessageSquare
          size={16}
          strokeWidth={hasComment ? 2.5 : 1.8}
          fill={hasComment ? activeColor : 'none'}
        />
      </button>
    </div>
  );
};

export default memo(FeedbackButtons);
